import { FC, useMemo, useState, useRef, useEffect, useCallback } from "react";

import { Nullable, useDriverControllerGetAll } from "service";

import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { OpenInFull } from "@mui/icons-material";
import ActionMenu from "./action-menu/action-menu";
import { useDialogStore } from "../../../state/dialog-store";
import {
  formatNumberToMinutes,
  paletteColorStringRenderer,
} from "../../../utils/string-helpers";
import { makeSxStyles, ControlledSelect, ControlledSelectOption } from "ui";
import useRegionManagementStore from "../../../state/region-management-store";
import HoveredAddress from "./hovered-address/hovered-address";
import {
  Active,
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  Over,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ClickedAddress } from "../../../shared/hooks/use-address-click-handler";
import useHoverAddressDetails from "../../../hooks/use-hover-address-details";
import { useRegionManagementDragState } from "../../../hooks/use-region-management-drag-state";
import { findContainer } from "../../../shared/utils/region-dnd";
import RegionAddressesLayout from "./addresses-layout";
import LoadingModal from "../route-planner/loading-modal";
import DropPositionTooltip from "../components/generic-table/drop-position-tooltip";
import { useDragTooltip } from "../../../shared/hooks/useDragTooltip";
import { TIMETABLE_ITEM_WIDTH } from "../route-planner/helpers/timetable";
import CurrentTimeIndicator from "../route-planner/live/current-time-indicator";
import DriverControlledSelect from "../route-planner/driver-controlled-select";
import { generateHourOptions } from "../../../utils/date-time";
import useTimetableZoomStore from "../../../state/timetable-zoom-store";
import TimetableZoomControls from "../route-planner/components/timetable-zoom-controls";

// Import helper functions from route planner
import {
  getMinAndMaxTime,
  generateHourlySlots,
  calculateLeftPosition,
} from "../route-planner/helpers/timetable";
import { AdminRegionResDto } from "service/api/estabrookSchemas";

// Generate timeline based on actual region start times
const generateRegionHourlySlots = (regions: AdminRegionResDto[]): string[] => {
  // Convert regions to format compatible with route planner helper
  const routesForTimeCalculation = regions
    .filter((region) => region.startTime && region.regionETA)
    .map((region) => ({
      startTime: region.startTime,
      travelDetails: {
        serviceTimeMin: parseFloat(region.regionETA || "60"),
      },
    }));

  if (routesForTimeCalculation.length === 0) {
    // Fallback to standard 10-hour timeline if no regions have start times
    const hours: string[] = [];
    for (let hour = 8; hour <= 18; hour++) {
      hours.push(
        new Date(1970, 0, 1, hour).toLocaleTimeString([], {
          hour: "numeric",
          hour12: true,
        }),
      );
    }
    return hours;
  }

  return generateHourlySlots(routesForTimeCalculation as any);
};

// Generate headers for region management table
const generateRegionHeaders = (
  useHourlySlots = false,
  isMoreCollapsed = false,
  onToggleMore?: () => void,
  hiddenColumns: Set<string> = new Set(),
  isHeaderHidden = false,
) => {
  const allHeaders = [
    {
      label: "",
      value: "drag-handle",
      size: "3vw",
      minWidth: "48px",
    },
    {
      label: useHourlySlots ? "" : "Region",
      value: "name",
      size: "12vw",
      minWidth: "120px",
    },
    {
      label: useHourlySlots ? "" : "Driver",
      value: "driver",
      size: "10vw",
      minWidth: "140px",
    },
    {
      label: useHourlySlots ? "" : "Start Time",
      value: "startTime",
      size: "5vw",
      minWidth: "70px",
    },
    {
      label: useHourlySlots ? "" : "Total EST",
      value: "totalEstimation",
      size: "8vw",
      minWidth: "110px",
    },
    {
      label: useHourlySlots ? (
        <Box sx={{ paddingLeft: "10px" }}>Addresses</Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Addresses
          <TimetableZoomControls />
        </Box>
      ),
      value: "addresses",
      size: useHourlySlots ? "unset" : "50vw",
      minWidth: "550px",
      customCSS: {
        borderLeft: "1px solid rgba(81, 81, 81, 1)",
        paddingLeft: "8px",
      },
    },
  ];

  // Add More column with conditional visibility logic
  // Determine if content should be shown
  // Header row (useHourlySlots=false): always show content
  // Timeline row (useHourlySlots=true): show content only when scrolled
  const shouldShowContent =
    !useHourlySlots || (useHourlySlots && isHeaderHidden);

  // Determine if column should be visible (not just a placeholder)
  // Header row: always visible
  // Timeline row: visible only when scrolled OR expanded
  const shouldBeVisible =
    !useHourlySlots || (useHourlySlots && (isHeaderHidden || !isMoreCollapsed));

  allHeaders.push({
    label: shouldShowContent ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        More
        <IconButton
          size="small"
          onClick={onToggleMore}
          sx={{
            padding: "2px",
            color: isMoreCollapsed ? "#C5C6C9" : "#FFFFFF",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <OpenInFull sx={{ fontSize: "16px" }} />
        </IconButton>
      </Box>
    ) : (
      ""
    ),
    value: "actions",
    size: shouldBeVisible ? "5vw" : "0px",
    minWidth: shouldBeVisible ? "50px" : "0px",
    customCSS: (useHourlySlots
      ? shouldBeVisible
        ? isMoreCollapsed
          ? {
              borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
              borderBottom: "none",
              position: "sticky" as const,
              right: "0",
              zIndex: 6,
              backgroundColor: "#1E2022",
            }
          : {
              position: "sticky" as const,
              right: "0",
              zIndex: 6,
              backgroundColor: "#1E2022",
              borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
              borderBottom: "none",
            }
        : {
            width: "0px",
            minWidth: "0px",
            padding: "0px",
            borderLeft: "none",
            borderBottom: "none",
            overflow: "hidden",
          }
      : {
          position: "sticky" as const,
          right: "0",
          borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
          borderBottom: "none",
          backgroundColor: "#1E2022",
        }) as any,
  } as any);

  // Filter out hidden columns
  return allHeaders.filter((header) => !hiddenColumns.has(header.value));
};

export interface RegionManagementTableProps {
  data?: AdminRegionResDto[];
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
  onClickedAddress: (value: ClickedAddress) => void;
  onRegionResequence: (id: string) => void;
  onChangeServiceDuration: (id: string) => void;
  onDeleteRegion: (props: { pathParams: { id: string } }) => void;
  handleRowClick: (id: string) => void;
  onSelectRegionDriver: (regionId: string, driverId: string) => void;
  onSelectStartTime: (regionId: string, startTime: string) => void;
  onUpdateRegionPriority: (regionId: string, priority: number) => void;
  isLoading: boolean;
}

const RegionManagementTable: FC<RegionManagementTableProps> = (props) => {
  const {
    data = [],
    sortOptions,
    sortOptionsChange,
    isLoading,
    onDeleteRegion,
    onClickedAddress,
    onRegionResequence,
    handleRowClick,
    onChangeServiceDuration,
    onSelectRegionDriver,
    onSelectStartTime,
    onUpdateRegionPriority,
  } = props;
  const [isDragging, setIsDragging] = useState(false);
  const { activeTableRowIndex, setRegions, regions, findAddress } =
    useRegionManagementStore();
  const { setDialog } = useDialogStore();
  const { palette } = useTheme();
  const styles = useStyles();
  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    setViewportWidth,
    setZoomLevel,
    setActualTimelineHours,
  } = useTimetableZoomStore();

  // More column collapse state
  const [isMoreCollapsed, setIsMoreCollapsed] = useState(true);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);

  // Column visibility state for draggable divider
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [dividerPosition, setDividerPosition] = useState<number | null>(null);
  const [dragDividerPosition, setDragDividerPosition] = useState<number | null>(
    null,
  );
  const tableRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    isDragging: boolean;
    rafId: number | null;
    lastHiddenColumns: Set<string>;
    columnBoundaries: Map<string, { left: number; right: number }>;
    previousHiddenColumns: Set<string>;
    startX: number | null;
    isInitializing: boolean;
  }>({
    isDragging: false,
    rafId: null,
    lastHiddenColumns: new Set(),
    columnBoundaries: new Map(),
    previousHiddenColumns: new Set(),
    startX: null,
    isInitializing: false,
  });

  // Initialize zoom to show 24 hours by default (1.0 = 100%)
  useEffect(() => {
    // Set a reasonable viewport width (will be updated by actual measurements)
    setViewportWidth(1200);

    // Start with zoom level 1.0 which shows 24 hours
    setZoomLevel(1.0);
  }, [setViewportWidth, setZoomLevel]);

  // Update actual timeline hours when data changes to set minimum zoom
  useEffect(() => {
    if (data.length > 0) {
      // Get the actual timeline hours using the original function
      const actualTimelineSlots = generateRegionHourlySlots(data);
      // Use actual hours (don't force minimum 24)
      const actualHours = actualTimelineSlots.length;
      setActualTimelineHours(actualHours);
      // The store will automatically calculate minimum zoom as 24 / actualHours
    } else {
      // Default to 24 hours if no regions
      setActualTimelineHours(24);
    }
  }, [data, setActualTimelineHours]);

  // Handle ESC key to cancel drag
  useEffect(() => {
    if (!isDragging) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleDragCancel();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isDragging]);

  // Handle mouse wheel zoom with Ctrl key - only when over timetable
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      // Check if the event target is within the timetable by looking for table elements
      const target = e.target as Element;
      const isWithinTable =
        target.closest("table") ||
        target.closest('[data-testid*="table"]') ||
        target.closest(".MuiTableContainer-root");

      if (!isWithinTable) {
        return;
      }

      // Only zoom when Ctrl key is pressed
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        if (e.deltaY < 0) {
          // Zoom in
          zoomIn();
        } else {
          // Zoom out
          zoomOut();
        }
      }
    },
    [zoomIn, zoomOut],
  );

  // Handle keyboard shortcuts - only when focused on table elements
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Check if focus is on a table element
      const target = e.target as Element;
      const isFocusedOnTable =
        target.closest("table") ||
        target.closest('[data-testid*="table"]') ||
        target.closest(".MuiTableContainer-root") ||
        target.tagName === "BODY"; // Also allow when no specific element is focused

      if (!isFocusedOnTable) {
        return;
      }

      // Ctrl/Cmd + Plus/Equals: Zoom In
      if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "=")) {
        e.preventDefault();
        zoomIn();
      }

      // Ctrl/Cmd + Minus: Zoom Out
      if ((e.ctrlKey || e.metaKey) && e.key === "-") {
        e.preventDefault();
        zoomOut();
      }

      // Ctrl/Cmd + 0: Reset Zoom
      if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        setZoomLevel(1.0);
      }
    },
    [zoomIn, zoomOut, setZoomLevel],
  );

  // Add wheel and keyboard event listeners
  useEffect(() => {
    const wheelOptions = { passive: false };
    window.addEventListener("wheel", handleWheel, wheelOptions);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleWheel, handleKeyDown]);

  // Toggle handler for More column
  const handleToggleMore = useCallback(() => {
    const newState = !isMoreCollapsed;
    setIsMoreCollapsed(newState);
    // If manually toggling, disable auto-collapse behavior
    if (isAutoCollapsed) {
      setIsAutoCollapsed(false);
    }
  }, [isMoreCollapsed, isAutoCollapsed]);

  // Handle scroll detection to auto-collapse More column at the end and detect header visibility
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check if this is a table container scroll
      if (
        target.classList?.contains("MuiTableContainer-root") ||
        target?.closest(".MuiTableContainer-root")
      ) {
        const scrollContainer = target.classList.contains(
          "MuiTableContainer-root",
        )
          ? target
          : target.closest(".MuiTableContainer-root");

        if (scrollContainer) {
          const { scrollLeft, scrollWidth, clientWidth, scrollTop } =
            scrollContainer;
          const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10; // 10px threshold

          // Check if the header is scrolled out of view (first header row height is typically 70px)
          setIsHeaderHidden(scrollTop > 70);

          // Recalculate divider position on scroll
          if (tableRef.current && !dragStateRef.current.isDragging) {
            const addressesHeader = tableRef.current.querySelector(
              '[data-header-value="addresses"]',
            );
            if (addressesHeader) {
              const tableRect = tableRef.current.getBoundingClientRect();
              const headerRect = addressesHeader.getBoundingClientRect();
              const newPosition = headerRect.left - tableRect.left - 4;
              setDividerPosition(newPosition);
            }
          }

          if (isAtEnd && !isMoreCollapsed) {
            // At the end, auto-collapse
            setIsMoreCollapsed(true);
            setIsAutoCollapsed(true);
          } else if (!isAtEnd && isAutoCollapsed) {
            // Not at the end anymore and was auto-collapsed, expand it
            setIsMoreCollapsed(false);
            setIsAutoCollapsed(false);
          }
        }
      }
    };

    // Add scroll listener to capture phase to catch all scroll events
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isMoreCollapsed, isAutoCollapsed]);

  // Divider drag handlers
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent multiple simultaneous drag operations or rapid clicks
      if (
        dragStateRef.current.isDragging ||
        dragStateRef.current.isInitializing
      ) {
        return;
      }

      if (!tableRef.current) return;

      // Mark as initializing to prevent rapid clicks
      dragStateRef.current.isInitializing = true;

      // Store the current hidden state and mouse position
      dragStateRef.current.previousHiddenColumns = new Set(hiddenColumns);
      dragStateRef.current.startX = e.clientX;

      // Temporarily show all columns to cache their boundaries
      // We need to do this in a way that doesn't cause a visible flash
      const previousHidden = hiddenColumns;
      setHiddenColumns(new Set());

      // Wait for the next frame to ensure DOM has updated with all columns visible
      requestAnimationFrame(() => {
        if (!tableRef.current || !dragStateRef.current.isInitializing) {
          // If initialization was cancelled, restore previous state
          setHiddenColumns(previousHidden);
          dragStateRef.current.isInitializing = false;
          return;
        }

        // Cache column boundaries with all columns visible
        const tableRect = tableRef.current.getBoundingClientRect();
        const headerCells = tableRef.current.querySelectorAll("thead th");
        const boundaries = new Map<string, { left: number; right: number }>();

        // Define the order of columns (right to left from Addresses)
        // Region column is excluded - it should never be hidden
        const columnOrder = ["totalEstimation", "startTime", "driver"];

        headerCells.forEach((cell) => {
          const headerValue = cell.getAttribute("data-header-value");
          if (headerValue && columnOrder.includes(headerValue)) {
            const cellRect = cell.getBoundingClientRect();
            boundaries.set(headerValue, {
              left: cellRect.left - tableRect.left,
              right: cellRect.right - tableRect.left,
            });
          }
        });

        dragStateRef.current.columnBoundaries = boundaries;
        dragStateRef.current.isDragging = true;
        dragStateRef.current.isInitializing = false;
        setIsDraggingDivider(true);

        // Get the scroll container to account for scroll offset
        const scrollContainer = tableRef.current.closest(
          ".MuiTableContainer-root",
        ) as HTMLElement | null;
        const currentScrollOffset = scrollContainer?.scrollLeft || 0;

        // Immediately calculate and apply the correct hidden state based on current mouse position
        const mouseX = e.clientX - tableRect.left + currentScrollOffset;
        const newHiddenColumns = new Set<string>();

        // Sort columns by their right edge position (left to right)
        const sortedColumns = Array.from(boundaries.entries()).sort(
          (a, b) => a[1].right - b[1].right,
        );

        // Hide columns from right to left based on mouse position
        for (const [columnName, bounds] of sortedColumns) {
          const midpoint = (bounds.left + bounds.right) / 2;
          if (mouseX < midpoint) {
            newHiddenColumns.add(columnName);
          }
        }

        dragStateRef.current.lastHiddenColumns = newHiddenColumns;
        setHiddenColumns(newHiddenColumns);
        setDragDividerPosition(mouseX - 4);
      });
    },
    [hiddenColumns],
  );

  const handleDividerMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStateRef.current.isDragging || !tableRef.current) return;

    // Cancel any pending animation frame
    if (dragStateRef.current.rafId !== null) {
      cancelAnimationFrame(dragStateRef.current.rafId);
    }

    // Use requestAnimationFrame for smooth updates
    dragStateRef.current.rafId = requestAnimationFrame(() => {
      if (!tableRef.current) return;

      // Get the scroll container to account for scroll offset
      const scrollContainer = tableRef.current.closest(
        ".MuiTableContainer-root",
      ) as HTMLElement | null;
      const currentScrollOffset = scrollContainer?.scrollLeft || 0;

      const tableRect = tableRef.current.getBoundingClientRect();
      let mouseX = e.clientX - tableRect.left + currentScrollOffset;

      // Calculate min and max boundaries from hideable columns
      const boundaries = dragStateRef.current.columnBoundaries;
      const sortedColumns = Array.from(boundaries.entries()).sort(
        (a, b) => a[1].left - b[1].left,
      );

      if (sortedColumns.length > 0) {
        const leftmostColumn = sortedColumns[0][1]; // Driver column
        const rightmostColumn = sortedColumns[sortedColumns.length - 1][1]; // Total EST column
        const minBoundary = leftmostColumn.left;
        const maxBoundary = rightmostColumn.right;

        // Clamp mouseX to stay within the hideable column boundaries
        mouseX = Math.max(minBoundary, Math.min(maxBoundary, mouseX));
      }

      // Update visual divider position to follow mouse (clamped)
      setDragDividerPosition(mouseX - 4);

      const newHiddenColumns = new Set<string>();

      // Sort columns by their right edge position (left to right)
      const sortedColumnsByRight = Array.from(
        dragStateRef.current.columnBoundaries.entries(),
      ).sort((a, b) => a[1].right - b[1].right);

      // Hide columns from right to left based on mouse position
      for (const [columnName, bounds] of sortedColumnsByRight) {
        const midpoint = (bounds.left + bounds.right) / 2;
        if (mouseX < midpoint) {
          newHiddenColumns.add(columnName);
        }
      }

      // Only update if the set actually changed
      const hasChanged =
        newHiddenColumns.size !== dragStateRef.current.lastHiddenColumns.size ||
        Array.from(newHiddenColumns).some(
          (col) => !dragStateRef.current.lastHiddenColumns.has(col),
        );

      if (hasChanged) {
        dragStateRef.current.lastHiddenColumns = newHiddenColumns;
        setHiddenColumns(newHiddenColumns);
      }
    });
  }, []);

  const handleDividerMouseUp = useCallback((e: MouseEvent) => {
    dragStateRef.current.isDragging = false;
    dragStateRef.current.isInitializing = false;
    if (dragStateRef.current.rafId !== null) {
      cancelAnimationFrame(dragStateRef.current.rafId);
      dragStateRef.current.rafId = null;
    }
    setIsDraggingDivider(false);
    setDragDividerPosition(null);

    // Check if the mouse has moved significantly (more than 5 pixels)
    const hasMovedSignificantly =
      dragStateRef.current.startX !== null &&
      Math.abs(e.clientX - dragStateRef.current.startX) > 5;

    // If the mouse hasn't moved significantly, restore the previous hidden state
    if (!hasMovedSignificantly) {
      setHiddenColumns(dragStateRef.current.previousHiddenColumns);
    }

    // Reset stored values
    dragStateRef.current.startX = null;
    dragStateRef.current.previousHiddenColumns = new Set();

    // Recalculate divider position after drag ends and layout stabilizes
    setTimeout(() => {
      if (tableRef.current) {
        const addressesHeader = tableRef.current.querySelector(
          '[data-header-value="addresses"]',
        );
        if (addressesHeader) {
          const tableRect = tableRef.current.getBoundingClientRect();
          const headerRect = addressesHeader.getBoundingClientRect();
          const newPosition = headerRect.left - tableRect.left - 4;
          setDividerPosition(newPosition);
        }
      }
    }, 100);
  }, []);

  // Add/remove divider drag event listeners
  useEffect(() => {
    if (isDraggingDivider) {
      document.addEventListener("mousemove", handleDividerMouseMove);
      document.addEventListener("mouseup", handleDividerMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleDividerMouseMove);
        document.removeEventListener("mouseup", handleDividerMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
    return undefined;
  }, [isDraggingDivider, handleDividerMouseMove, handleDividerMouseUp]);

  // Calculate divider position whenever table layout changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const updateDividerPosition = () => {
      // Don't update position while dragging
      if (dragStateRef.current.isDragging) return;

      // Debounce to prevent excessive updates
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (tableRef.current && !dragStateRef.current.isDragging) {
          const addressesHeader = tableRef.current.querySelector(
            '[data-header-value="addresses"]',
          );
          if (addressesHeader) {
            const tableRect = tableRef.current.getBoundingClientRect();
            const headerRect = addressesHeader.getBoundingClientRect();
            const newPosition = headerRect.left - tableRect.left - 4;

            // Always update to ensure divider appears
            setDividerPosition(newPosition);
          }
        }
      }, 50);
    };

    // Initial calculation with multiple attempts to ensure table is rendered
    const initialTimeout = setTimeout(updateDividerPosition, 100);
    const fallbackTimeout = setTimeout(updateDividerPosition, 300);
    const finalTimeout = setTimeout(updateDividerPosition, 500);

    // Recalculate on resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      if (dragStateRef.current.isDragging) return;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDividerPosition, 150);
    };

    window.addEventListener("resize", handleResize);

    // Use ResizeObserver with debounce - but skip during drag
    const observer = new ResizeObserver(() => {
      if (!dragStateRef.current.isDragging) {
        updateDividerPosition();
      }
    });

    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(fallbackTimeout);
      clearTimeout(finalTimeout);
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [hiddenColumns, data.length]);

  // Generate headers with collapse state
  const sanitizedHeader = useMemo(
    () =>
      generateRegionHeaders(
        false,
        isMoreCollapsed,
        handleToggleMore,
        hiddenColumns,
        isHeaderHidden,
      ),
    [isMoreCollapsed, handleToggleMore, hiddenColumns, isHeaderHidden],
  );

  // Fetch drivers data
  const { data: driversData, isLoading: isLoadingDrivers } =
    useDriverControllerGetAll({
      queryParams: { limit: 1000, isAvailable: true, hasVehicle: true },
    });

  // Generate driver options
  const driverOptions: ControlledSelectOption[] = useMemo(() => {
    return (
      driversData?.data?.reduce(
        (acc, driver) => {
          if (driver.id && driver.name) {
            acc.push({
              value: driver.id,
              label: driver.name,
              selectedLabel: driver.name,
            });
          }
          return acc;
        },
        [
          {
            label: "No Driver",
            value: "No Driver",
          },
        ] as ControlledSelectOption[],
      ) || []
    );
  }, [driversData]);

  // Generate hour options for start time
  const hourOptions = useMemo(() => generateHourOptions(false, 30), []);

  // Create a stable dependency for start times
  const startTimesDependency = useMemo(() => {
    return data.map((region) => region.startTime).join(",");
  }, [data]);

  // Create timeline header for region management
  const renderRegionHourlySlots = useMemo(() => {
    const hourlySlotsData = generateRegionHourlySlots(data);
    const hourlySlots = hourlySlotsData.map((hourSlot, index) => (
      <Typography
        key={`${hourSlot}-${index}`}
        variant="caption"
        fontWeight="500"
        color="#C5C6C9"
        width={TIMETABLE_ITEM_WIDTH * zoomLevel}
        sx={{
          fontSize: "11px",
          textAlign: "left",
          whiteSpace: "nowrap",
        }}
      >
        {hourSlot}
      </Typography>
    ));

    // Calculate actual min/max time from regions with start times
    const regionsWithStartTime = data.filter(
      (region) => region.startTime && region.regionETA,
    );
    const routesForTimeCalculation = regionsWithStartTime.map((region) => ({
      startTime: region.startTime,
      travelDetails: { serviceTimeMin: parseFloat(region.regionETA || "60") },
    }));

    const minMaxTime =
      routesForTimeCalculation.length > 0
        ? getMinAndMaxTime(routesForTimeCalculation as any)
        : { min: "08:00", max: "18:00" };

    return (
      <Box sx={{ position: "relative", paddingLeft: "10px" }}>
        <Box sx={styles.hourlySlotsContainer}>{hourlySlots}</Box>
        <CurrentTimeIndicator
          routesMinAndMaxStartTime={minMaxTime}
          totalTimelineMinutes={hourlySlotsData.length * 60}
          totalTableItems={data?.length || 0}
        />
      </Box>
    );
  }, [data, styles.hourlySlotsContainer, startTimesDependency, zoomLevel]);

  // Create timeline header
  const timelineHeader = useMemo(() => {
    return generateRegionHeaders(
      true,
      isMoreCollapsed,
      handleToggleMore,
      hiddenColumns,
      isHeaderHidden,
    ).map((header) => ({
      ...header,
      label:
        header.value === "addresses" ? renderRegionHourlySlots : header.label,
    }));
  }, [
    renderRegionHourlySlots,
    isMoreCollapsed,
    handleToggleMore,
    hiddenColumns,
    isHeaderHidden,
  ]);

  const [sourceRegionId, setSourceRegionId] = useState<string | undefined>(
    undefined,
  );
  const fadeRef = useRef<HTMLDivElement>(null);

  const {
    moveOrderOnDragOver,
    changeCustomerOrderStatus,
    isUpdating,
    regionsBeforeDrag,
    resetRegionsBeforeDrag,
  } = useRegionManagementDragState({
    sourceRegionId,
  });

  const {
    handleBoxMouseLeave,
    handleBoxMouseEnter,
    handleMouseLeave,
    handleMouseEnter,
    isHovering,
    hoverPosition,
    hoveredAddress,
  } = useHoverAddressDetails({ allowTooltip: !isDragging, findAddress });

  const handleDeleteRegion = (name: string, id: string) => {
    setDialog({
      title: "Delete Region",
      description: `Are you sure you want to delete region ${name || ""}?<br />This will unassign all linked addresses and cannot be undone.`,
      confirmText: "Delete",
      withCancelAction: true,
      cancelText: "Cancel",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        onDeleteRegion({ pathParams: { id } });
        onClose();
      },
    });
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminRegionResDto>
  > = useMemo(
    () => ({
      name: ({
        headerValue,
        data: { id: regionId, name, color },
        setActiveRow,
      }) => ({
        label: (
          <Box
            sx={styles.regionNameWrapper}
            onClick={() => {
              handleRowClick(regionId);
              const index = data.findIndex(({ id }) => id === regionId);
              setActiveRow(index);
            }}
          >
            <Box
              sx={styles.regionNameColor}
              bgcolor={paletteColorStringRenderer(palette, color.toLowerCase())}
            />
            {name}
          </Box>
        ),
        value: headerValue,
      }),
      driver: ({ headerValue, data: { id, suggestedDrivers } }) => {
        // Get the first suggested driver (current driver)
        const currentDriver = suggestedDrivers?.[0];
        const driverId = currentDriver?.driverId;

        // Find the driver data from the API response
        const driverData =
          driverId && driverId !== "NO_DRIVER"
            ? driversData?.data?.find((d) => d.id === driverId)
            : undefined;

        return {
          label: isLoadingDrivers ? (
            <CircularProgress size={20} />
          ) : (
            <DriverControlledSelect
              driverOptions={driverOptions}
              availableForAnyDriver={false}
              driver={
                driverData
                  ? {
                      id: driverData.id!,
                      name: driverData.name!,
                    }
                  : undefined
              }
              handleDriverChange={(value) => {
                onSelectRegionDriver(id, value);
              }}
              isDisconnected={false}
            />
          ),
          value: headerValue,
        };
      },
      startTime: ({ headerValue, data: { id, startTime } }) => {
        return {
          label: (
            <ControlledSelect
              value={startTime || ""}
              onChange={(value) => onSelectStartTime(id, value)}
              options={hourOptions}
              placeholder="Select Start Time"
              sx={{
                "&.MuiSelect-filled": {
                  padding: "2px 32px 2px 8px !important",
                  minHeight: "unset !important",
                  height: "auto !important",
                },
                "& .MuiSelect-select": {
                  padding: "2px 32px 2px 8px !important",
                  minHeight: "unset !important",
                  height: "auto !important",
                },
                "& .MuiSelect-icon": {
                  right: "8px !important",
                },
              }}
            />
          ),
          value: headerValue,
        };
      },
      totalEstimation: ({ headerValue, data: { regionETA } }) => ({
        label: regionETA ? formatNumberToMinutes(parseFloat(regionETA)) : "",
        value: headerValue,
      }),
      addresses: ({
        headerValue,
        data: { id, locations, color, startTime, suggestedDrivers },
      }) => {
        // Calculate position on timeline if region has startTime
        const regionsWithStartTime = data.filter(
          (region) => region.startTime && region.regionETA,
        );
        const routesForTimeCalculation = regionsWithStartTime.map((region) => ({
          startTime: region.startTime,
          travelDetails: {
            serviceTimeMin: parseFloat(region.regionETA || "60"),
          },
        }));

        const minMaxTime =
          routesForTimeCalculation.length > 0
            ? getMinAndMaxTime(routesForTimeCalculation as any)
            : { min: "08:00", max: "18:00" };

        const leftPosition = startTime
          ? calculateLeftPosition(
              startTime,
              minMaxTime.min,
              undefined, // Use default TIMETABLE_ITEM_WIDTH
              zoomLevel,
            )
          : 0;

        // Offset to move the starting point outside the timeline start
        // Starting point dot (8px) + line (4px) = 12px
        const STARTING_POINT_OFFSET = 12;
        const adjustedLeftPosition = startTime
          ? leftPosition - STARTING_POINT_OFFSET
          : 0;

        // Get the first suggested driver (current driver) for tooltip
        const currentDriver = suggestedDrivers?.[0];
        const driverId = currentDriver?.driverId;

        // Find the driver data from the API response
        const driverData =
          driverId && driverId !== "NO_DRIVER"
            ? driversData?.data?.find((d) => d.id === driverId)
            : undefined;

        return {
          label: (
            <Box
              sx={{
                position: "relative",
                ...(startTime && {
                  left: adjustedLeftPosition,
                  maxWidth: `calc(100% - ${adjustedLeftPosition}px)`,
                }),
              }}
            >
              <RegionAddressesLayout
                locations={locations}
                regionId={id}
                color={color?.toLowerCase() ?? ""}
                onClickedAddress={(clickedAddress: ClickedAddress) =>
                  onClickedAddress({
                    e: clickedAddress.e,
                    address: clickedAddress.address,
                  })
                }
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                showStartingPoint={!!startTime}
                driverFullName={driverData?.name}
                driverId={driverId}
                driverStartingPointAddress={driverData?.startingPoint?.address}
                isPlanned={true}
              />
            </Box>
          ),
          value: headerValue,
          customCSS: {
            borderLeft: "1px solid rgba(81, 81, 81, 1)",
            position: "relative" as const,
          },
        };
      },
      actions: ({ headerValue, data: { id, name } }) => ({
        label: (
          <ActionMenu
            id={id}
            onResequenceRoute={() => onRegionResequence(id)}
            onDeleteRegion={() => handleDeleteRegion(name, id)}
            onChangeServiceTime={() => onChangeServiceDuration(id)}
          />
        ),
        value: headerValue,
        customCSS: isMoreCollapsed
          ? {}
          : {
              position: "sticky" as const,
              right: "0",
              zIndex: 1,
              backgroundColor: "#1E2022",
              borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
              borderBottom: "none",
            },
      }),
    }),
    [
      handleMouseEnter,
      handleMouseLeave,
      isHovering,
      onClickedAddress,
      hoveredAddress,
      driverOptions,
      driversData,
      isLoadingDrivers,
      hourOptions,
      onSelectRegionDriver,
      onSelectStartTime,
      isMoreCollapsed,
    ],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setSourceRegionId(String(active.data.current?.regionId));
    setIsDragging(true);
  };

  const {
    setDropIndices,
    setDragDetails,
    dropPosition,
    dropIndices,
    currentDraggedRegion,
    currentDraggedRegionLocation,
  } = useDragTooltip("region");

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    if (!over) {
      setDropIndices(null);
      return;
    }

    if (active.data.current?.type === "order") {
      const newRegions = moveOrderOnDragOver(event);

      setDragDetails(over, active, regions);

      if (newRegions) {
        setRegions(newRegions);
      }
    }
  };

  const changeRegionPriority = (active: Active, over: Over | null) => {
    const oldIndex = data.findIndex((region) => region.id === active.id);
    const newIndex = data.findIndex((region) => region.id === over?.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const newRegions = arrayMove(regions, oldIndex, newIndex);
      setRegions(newRegions);
      onUpdateRegionPriority(active.id.toString(), newIndex);
    }
  };

  const handleDragCancel = () => {
    // Reset drag state
    setIsDragging(false);
    setSourceRegionId(undefined);
    setDropIndices(null);

    // Restore to original state before drag
    if (regionsBeforeDrag.length > 0) {
      setRegions(regionsBeforeDrag);
      resetRegionsBeforeDrag();
    }
    // Note: Do NOT call resetAllSelected() - maintain selection
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setIsDragging(false);
    setSourceRegionId(undefined);
    setDropIndices(null);

    // Check if drop target is invalid (outside addresses column or on non-droppable area)
    const isInvalidDropTarget =
      !over ||
      (active.data.current?.type === "order" &&
        !over.data?.current?.regionId && // Not over a drop zone
        !findContainer(over.id, regions)); // Not over another order

    if (isInvalidDropTarget) {
      // Restore to original state before drag
      if (regionsBeforeDrag.length > 0) {
        setRegions(regionsBeforeDrag);
        resetRegionsBeforeDrag();
      }
      // Note: Do NOT call resetAllSelected() - maintain selection
      return;
    }

    // Allow same-ID drops if the drag-over logic positioned it correctly
    // (This happens when dropping at the end of a region)

    if (active.data.current?.type === "order") {
      changeCustomerOrderStatus(event);
    } else if (
      active.data.current?.type === "row" ||
      !active.data.current?.type
    ) {
      // Handle region row dragging (either explicit row type or no type)
      changeRegionPriority(active, over);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),

    useSensor(MouseSensor),
    useSensor(TouchSensor),
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragCancel={handleDragCancel}
      >
        <Box
          ref={tableRef}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <GenericTable
            sx={styles.regionManagementTableStyle}
            headers={sanitizedHeader}
            secondHeaders={timelineHeader}
            renderRowLabel={renderRowLabel}
            isHeaderSticky={true}
            data={regions}
            sortOptions={sortOptions}
            onSortChange={sortOptionsChange}
            isLoading={isLoading}
            shouldSetActiveRow={false}
            defaultActiveRow={activeTableRowIndex}
            draggable={true}
            compactCellPadding
          />
          {/* Draggable divider on the border of Addresses column */}
          {dividerPosition !== null && (
            <Box
              onMouseDown={handleDividerMouseDown}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseLeave={(e) => e.stopPropagation()}
              sx={{
                position: "absolute",
                top: "20px",
                bottom: "8px",
                width: "12px",
                left: `${isDraggingDivider && dragDividerPosition !== null ? dragDividerPosition - 2 : dividerPosition - 2}px`,
                cursor: "col-resize",
                zIndex: 10000,
                pointerEvents: "auto",
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
                overflow: "hidden",
                transition: isDraggingDivider
                  ? "none"
                  : "left 0.2s ease-out, background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
                "&:hover::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "2px",
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                },
                "&:active": {
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                },
                ...(isDraggingDivider && {
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                  },
                }),
              }}
            />
          )}
          {/* Overlay to prevent row hover during drag */}
          {isDraggingDivider && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                cursor: "col-resize",
                userSelect: "none",
              }}
            />
          )}
        </Box>
        <HoveredAddress
          hoveredAddress={hoveredAddress}
          isHovering={isHovering}
          handleBoxMouseEnter={handleBoxMouseEnter}
          handleBoxMouseLeave={handleBoxMouseLeave}
          hoverPosition={hoverPosition}
          fadeRef={fadeRef}
        />
        <DropPositionTooltip
          isVisible={isDragging}
          position={dropPosition}
          beforeIndex={dropIndices?.before ?? 0}
          afterIndex={dropIndices?.after ?? 0}
          totalItems={currentDraggedRegion?.locations.length ?? 0}
          regionName={currentDraggedRegion?.name ?? ""}
          regionColor={currentDraggedRegion?.color ?? ""}
          serviceTime={
            (currentDraggedRegionLocation as any)?.location?.serviceTime
              ? formatNumberToMinutes(
                  (currentDraggedRegionLocation as any)?.location?.serviceTime,
                )
              : ""
          }
        />
      </DndContext>
      <LoadingModal isLoading={isUpdating} />
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  regionManagementTableStyle: {
    mt: "6px",
    flex: "1 1 75px",
    maxHeight: "fit-content",
    borderRadius: "8px !important",
    overflow: "auto",
    "& table": {
      paddingBottom: "8px",
      borderRadius: "8px",
    },
    "& thead tr:first-of-type th:first-of-type": {
      borderTopLeftRadius: "8px",
    },
    "& thead tr:first-of-type th:last-of-type": {
      borderTopRightRadius: "8px",
    },
    "& thead tr:first-of-type th[data-header-value='actions']": {
      borderTopRightRadius: "0px",
    },
    "& tbody tr:last-of-type td:first-of-type": {
      borderBottomLeftRadius: "8px",
    },
    "& tbody tr:last-of-type td:last-of-type": {
      borderBottomRightRadius: "8px",
    },
    "& tbody tr:last-of-type td[data-header-value='actions']": {
      borderBottomRightRadius: "0px",
    },
    "& tbody tr": {
      background: "#1A1C1E",
    },
    "& thead th": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
    "& thead tr:last-of-type th": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
    "& tbody td": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
  },
  regionNameWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
  },
  regionNameColor: {
    display: "inline-flex",
    width: "6px",
    height: "14px",
    borderRadius: "2px",
    marginRight: "4px",
  },
  hourlySlotsContainer: {
    display: "flex",
    alignItems: "center",
  },
}));

export default RegionManagementTable;
